import { useEffect, useState } from 'react';

/**
 * Whole seconds remaining until `target`, ticking down to zero.
 *
 * Used by the resend button, which the API will refuse before its `resendAfter` moment. The
 * server's timestamp is the source of truth rather than a local `setTimeout(30_000)`: a phone
 * that was locked for a minute, or a clock that drifted, must not end up with a button that
 * says "resend in 12s" forever or one that resends into a 429.
 */
export function useCountdown(target: string | Date | null): number {
  const targetMs = target === null ? null : new Date(target).getTime();
  const [remaining, setRemaining] = useState(() => secondsUntil(targetMs));

  useEffect(() => {
    setRemaining(secondsUntil(targetMs));
    if (targetMs === null) return;

    const timer = setInterval(() => {
      const next = secondsUntil(targetMs);
      setRemaining(next);
      if (next <= 0) clearInterval(timer);
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [targetMs]);

  return remaining;
}

function secondsUntil(targetMs: number | null): number {
  if (targetMs === null || Number.isNaN(targetMs)) return 0;
  return Math.max(0, Math.ceil((targetMs - Date.now()) / 1000));
}

/** `95` → `1:35`, `40` → `40s`. What a person waiting reads without doing arithmetic. */
export function formatWait(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes)}:${String(rest).padStart(2, '0')}`;
}
